import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import CountryLanguage from '../../../../../types/country-language/CountryLanguage';
import Country from '../../../../../types/country/Country';
import CountryWriteDTO from '../../../../../types/country/CountryWriteDTO';
import Language from '../../../../../types/language/Language';
import { IsAdminDirective } from '../../../directives/is-admin.directive';
import { RoleNamePipe } from '../../../pipes/role-name.pipe';
import { CountryService } from '../../../services/country.service';
import { LanguageService } from '../../../services/language.service';
import {
    UpdateCountryDialogComponent
} from '../../shared/countries/update-country-dialog/update-country-dialog.component';
import { PageHeaderComponent } from '../../shared/page-header/page-header.component';

@Component({
    selector: 'app-country-detail',
    templateUrl: './country-detail.component.html',
    styleUrls: [ './country-detail.component.scss' ],
    imports: [
        Button,
        RouterLink,
        PageHeaderComponent,
        UpdateCountryDialogComponent,
        RoleNamePipe,
        IsAdminDirective
    ]
})
export class CountryDetailComponent implements OnInit {
    public country: Country | null = null;
    public languages: Language[] = [];
    public editableCountry: CountryWriteDTO = new CountryWriteDTO({});
    public displayUpdateDialog = false;

    constructor(
        private route: ActivatedRoute,
        private countryService: CountryService,
        private languageService: LanguageService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService
    ) {
    }

    /**
     * Initializes the component.
     */
    public async ngOnInit(): Promise<void> {
        await this.loadCountry();
    }

    /**
     * Opens the edit dialog.
     */
    public openEditDialog(): void {
        this.editableCountry = new CountryWriteDTO(this.country ?? {});
        this.displayUpdateDialog = true;
    }

    /**
     * Reloads the country details.
     */
    public async reloadCountryDetails(): Promise<void> {
        await this.loadCountry();
    }

    /**
     * Confirms the deletion of the country.
     */
    public confirmDelete(): void {
        if (!this.country) {
            return;
        }

        this.confirmationService.confirm({
            message: `Are you sure you want to delete the country '${ this.country.name }'?`,
            header: 'Confirm Deletion',
            icon: 'pi pi-exclamation-triangle',
            accept: async () => {
                try {
                    await this.countryService.deleteCountry(this.country!.id);
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Deleted',
                        detail: 'Country deleted successfully',
                        life: 3000
                    });
                    // Navigate back to the countries page
                    window.history.back();
                } catch (error: any) {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
                }
            }
        });
    }

    /**
     * Loads the country details.
     */
    private async loadCountry(): Promise<void> {
        const id = this.route.snapshot.paramMap.get('id');
        if (id) {
            const idNumber = parseInt(id);
            try {
                // Fetch country details
                this.country = await this.countryService.getCountry(idNumber);

                // Fetch associated languages
                if (this.country.languages?.length) {
                    const languageIds = this.country.languages.map((cl: CountryLanguage) => cl.languageId);
                    this.languages = await this.languageService.getMultipleLanguages(languageIds);
                }
            } catch (error: any) {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
            }
        }
    }
}
